/* eslint-disable object-curly-newline */
import React, { Fragment, useRef } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { IconButton, mergeStyles } from '@fluentui/react';

import { ParentColumnPlacement } from '../utils/constants';
import { NameHelpId, CommonColumn, WithCustomizedSortIcon } from '../utils/common-columns';
import { WithAutoResizeIcon } from '../autoResizeHeader';

const ExpandOrCollapseIcon = (props) => {
  const { theKey: key, isExpanded, alignWithCampaignUI } = props;
  const style = alignWithCampaignUI ? { root: { zIndex: 4, width: 16, height: 16, transform: 'scale(0.8)' }, icon: { fontSize: 14, fontWeight: 'bold !important', color: '#323130' } }
    : { root: { zIndex: 4 }, icon: { fontSize: 13, fontWeight: 'bold !important' } };
  return (<IconButton
    iconProps={{ iconName: isExpanded ? props.subColumnProps.collapseIconName : props.subColumnProps.expandIconName }}
    ariaLabel={isExpanded ? `Collapse ${key}` : `Expand ${key}`}
    // set zIndex: 4 so the icon will be higher than the cellSizer
    styles={style}
    onClick={(e) => {
      e.stopPropagation();
      props.updateColumns(key);
    }}
  />);
};

ExpandOrCollapseIcon.propTypes = {
  theKey: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool,
  subColumnProps: PropTypes.shape({
    collapseIconName: PropTypes.string,
    expandIconName: PropTypes.string,
  }).isRequired,
  updateColumns: PropTypes.func.isRequired,
  alignWithCampaignUI: PropTypes.bool,
};

ExpandOrCollapseIcon.defaultProps = {
  isExpanded: undefined,
  alignWithCampaignUI: false,
};

const WithExpandOrCollapseIcon = (props) => {
  const { alignWithCampaignUI, alwaysExpanded } = props;
  const commonClassName = 'header-with-expand-or-collapse-icon';
  return alignWithCampaignUI ?
    (
      <Fragment>
        <span className={`${commonClassName} ${mergeStyles({ display: 'inline-flex', alignItems: 'center' })}`}>
          {alwaysExpanded ? null : <ExpandOrCollapseIcon {...props} />}
          {props.children}
        </span>
      </Fragment >
    ) :
    (
      <Fragment>
        <span className={commonClassName}>
          {props.children}
          {alwaysExpanded ? null : <ExpandOrCollapseIcon {...props} />}
        </span>
      </Fragment>
    );
};

WithExpandOrCollapseIcon.propTypes = {
  alignWithCampaignUI: PropTypes.bool,
  alwaysExpanded: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

WithExpandOrCollapseIcon.defaultProps = {
  alwaysExpanded: false,
  alignWithCampaignUI: false,
};

const SubColumnHeader = (props) => {
  const { idx, subColumns, headerPadding, columnIndex, subHelpId, column, withCustomizedSortIcon, headerContainerRef } = props;
  const row2Ref = useRef();

  return (
    <div
      ref={row2Ref}
      className={`row-2 ${mergeStyles({
        borderRight: idx < subColumns.length - 1 && '1px solid #EDEBE9',
        paddingLeft: headerPadding,
        paddingRight: headerPadding,
        display: 'flex',
        ...(subHelpId ? {
          alignItems: 'center',
          height: '40px',
          lineHeight: '32px',
        } : {}),
      })}`}
    >
      <WithAutoResizeIcon columnIndex={columnIndex} containerRef={headerContainerRef} columnHeaderRef={row2Ref}>
        <WithCustomizedSortIcon withCustomizedSortIcon={withCustomizedSortIcon} isSorted={column.isSorted} isSortedDescending={column.isSortedDescending}>
          <span
            className={`sub-column-name ${mergeStyles({
              overflow: 'hidden',
              ...(subHelpId ? {
              } : {
                textOverflow: 'ellipsis',
              }),
            })}`}
          >
            <NameHelpId column={{ name: column.name, helpId: subHelpId }} />
          </span>
        </WithCustomizedSortIcon>
      </WithAutoResizeIcon>
    </div>
  );
};

export function withSubColumns(WrappedComponent) {
  return class extends React.PureComponent {
    static propTypes = {
      columns: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
      ]).isRequired,
      items: PropTypes.oneOfType([
        PropTypes.array,
      ]).isRequired,
      onUpdateColumns: PropTypes.func,
      subColumnProps: PropTypes.shape({
        showSubColumns: PropTypes.bool,
        parentColumnPlacement: PropTypes.oneOf(_.values(ParentColumnPlacement)),
        expandIconName: PropTypes.string,
        collapseIconName: PropTypes.string,
      }),
      alignWithCampaignUI: PropTypes.bool,
      withCustomizedSortIcon: PropTypes.bool,
    };

    static defaultProps = {
      onUpdateColumns: _.noop,
      subColumnProps: {
        showSubColumns: false,
        parentColumnPlacement: ParentColumnPlacement.Center,
        expandIconName: 'Add',
        collapseIconName: 'Remove',
      },
      alignWithCampaignUI: false,
      withCustomizedSortIcon: false,
    };

    constructor(props) {
      super(props);
      this.headerContainerRef = React.createRef();
    }

    processSubColumnsConfigs() {
      const flattenColumns = [];
      let showSubColumns = false;
      _.each(this.props.columns, (column) => {
        if (!_.isEmpty(column.subColumns)) {
          const { key, subColumns, isExpanded, alwaysExpanded } = column;
          if (isExpanded || alwaysExpanded) {
            showSubColumns = true;
            let columnsWidth;
            _.each(subColumns, (subColumn, idx) => {
              const defaultColumnWidth = subColumn.minWidth || subColumn.name.length * 7;
              const currentFlattenColumnsIdx = flattenColumns.length;
              flattenColumns.push({
                ...column,
                ...subColumn,
                parentName: column.name,
                parentHelpId: column.helpId,
                subHelpId: subColumn.helpId,
                styles: {
                  root: {
                    borderRight: idx < subColumns.length - 1 && 'none !important',
                    textAlign: 'unset',
                  },
                  cellTitle: { padding: 0, display: 'inline' },
                  sortIcon: { display: 'none' },
                },
                minWidth: defaultColumnWidth,
                onRenderHeader: (props) => {
                  const columnWidth = props.column.currentWidth;
                  const contentWidth = props.column.name.length * 8 + 20; // eslint-disable-line no-mixed-operators
                  const headerPadding = 8;
                  if (idx === 0) {
                    columnsWidth = columnWidth || defaultColumnWidth;
                  } else {
                    columnsWidth += columnWidth || defaultColumnWidth;
                  }
                  const displayIdx = (() => {
                    switch (this.props.subColumnProps.parentColumnPlacement) {
                      case ParentColumnPlacement.Left:
                        return 1;
                      case ParentColumnPlacement.Right:
                        return subColumns.length - 1;
                      default:
                        return parseInt(subColumns.length / 2, 10);
                    }
                  })();

                  return (
                    <div className={`${key}-sub-column-${idx}`}>
                      <div className={`row-1 ${mergeStyles({ borderBottom: '2px solid #81868B' })}`}>
                        <span
                          className="sub-column-content"
                          // set the content of the $('.sub-column-content') to the center of sub-columns
                          style={{
                            ...(idx !== displayIdx ? { visibility: 'hidden' } : {
                              position: 'relative',
                              zIndex: 1, // set zIndex: 1 so the content will be higher than the next parent column and the content is clickable
                            }),
                            marginLeft: -columnsWidth / 2,
                            paddingLeft: (columnWidth || defaultColumnWidth) + headerPadding - contentWidth / 2, // eslint-disable-line no-mixed-operators
                          }}
                        >
                          <WithExpandOrCollapseIcon alwaysExpanded={alwaysExpanded} alignWithCampaignUI={this.props.alignWithCampaignUI} subColumnProps={this.props.subColumnProps} theKey={key} isExpanded={isExpanded} updateColumns={this.updateColumns}>
                            <span
                              className={`parent-column-name ${mergeStyles(this.props.alignWithCampaignUI ?
                                { display: idx !== displayIdx ? 'none' : 'inline-flex', alignItems: 'center', height: '32px', lineHeight: '32px' }
                                : { display: idx !== displayIdx && 'none' })}`}
                            >
                              <NameHelpId column={{ name: props.column.parentName, helpId: props.column.parentHelpId }} />
                            </span>
                          </WithExpandOrCollapseIcon>
                        </span>
                      </div>
                      <SubColumnHeader headerContainerRef={this.headerContainerRef} idx={idx} subColumns={subColumns} headerPadding={headerPadding} columnIndex={currentFlattenColumnsIdx} subHelpId={props.column.subHelpId} column={props.column} withCustomizedSortIcon={this.props.withCustomizedSortIcon} />
                    </div>
                  );
                },
              });
            });
          } else {
            flattenColumns.push({
              ...column,
              styles: {
                ...(this.props.withCustomizedSortIcon ? {
                  sortIcon: { display: 'none' },
                } : {}),
              },
              onRenderHeader: props => (
                <React.Fragment>
                  <WithExpandOrCollapseIcon alignWithCampaignUI={this.props.alignWithCampaignUI} subColumnProps={this.props.subColumnProps} theKey={key} isExpanded={isExpanded} updateColumns={this.updateColumns}>
                    <CommonColumn column={props.column} alignWithCampaignUI={this.props.alignWithCampaignUI} withCustomizedSortIcon={this.props.withCustomizedSortIcon} />
                  </WithExpandOrCollapseIcon>
                </React.Fragment>
              ),
            });
          }
        } else {
          flattenColumns.push(column);
        }
      });

      return {
        flattenColumns,
        showSubColumns,
      };
    }

    updateColumns = (updatedKey) => {
      const newColumns = _.map(this.props.columns, (column) => {
        if (column.key === updatedKey) {
          return {
            ...column,
            isExpanded: !column.isExpanded,
          };
        }
        return column;
      });

      this.props.onUpdateColumns(newColumns);
    };

    render() {
      const {
        flattenColumns,
        showSubColumns,
      } = this.processSubColumnsConfigs();
      return (
        <WrappedComponent
          {...this.props}
          columns={flattenColumns}
          headerContainerRef={this.headerContainerRef}
          subColumnProps={{
            showSubColumns,
            parentColumnPlacement: this.props.subColumnProps.parentColumnPlacement,
            expandIconName: this.props.subColumnProps.expandIconName,
            collapseIconName: this.props.subColumnProps.collapseIconName,
          }}
        />
      );
    }
  };
}
